<script setup>
import { DictationStatus, dictationMessage, dt, learnTerm, recordingExtension, soundDistance, useDictation } from '@addictivegroup/addictive-dictation'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { saveFilling } from '@/lib/saveFilling.js'
import { useSave } from '@/composables/useSave.js'
import { queueNoteFiles } from '@/lib/noteUploads.js'
import { formatBytes } from '@/lib/format.js'
import { useUi } from '@/stores/ui.js'
import { useMeta } from '@/stores/meta.js'
import RichTextEditor from '@/components/form/RichTextEditor.vue'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'

/**
 * Writes a note on a record (D-44): rich text with @-mentions; Ctrl+Enter saves. On a phone
 * (D-143) the note is dictated, and photos and films are taken with the camera or picked from
 * the gallery; they are kept on the device and sent with the note, also when it was written
 * without network.
 */
// replyTo {uuid, label}: a mention answered from its notification (D-257) — the note starts addressed to them.
const props = defineProps({ entity: { type: String, required: true }, uuid: { type: String, required: true }, replyTo: { type: Object, default: null } })
const emit = defineEmits(['saved'])
const { t } = useI18n()
const dictationError = (error) => ui.warn(dictationMessage(error))
const { report } = useSave()
const ui = useUi()
const meta = useMeta()
const body = ref(null)
watch(() => props.replyTo?.uuid, async (uuid) => {
  if (!uuid || body.value) return
  body.value = { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'mention', attrs: { id: `user:${uuid}`, label: props.replyTo.label || '' } }, { type: 'text', text: ' ' }] }] }
  await nextTick()
  editor.value?.focus()
}, { immediate: true })
const saving = ref(false)
const editor = ref(null)
const isPrivate = ref(false)
const files = ref([]) // {file, url, kind}
const photoInput = ref(null)
const videoInput = ref(null)
const fileInput = ref(null)
const MAX = 512 * 1024 * 1024

const dictation = useDictation({
  // What is said shows in the note as it is said (D-146); a phrase being written again is grey.
  onText: (text, options) => editor.value?.insert(text, options),
  onResolve: (id, text) => editor.value?.resolve(id, text),
  // The dictation's sound goes with the note, to listen to if something was misheard.
  onRecording: (blob) => {
    const stamp = new Date().toLocaleString('sv-SE').slice(0, 16).replace(':', '.')
    const file = new File([blob], `${dt('recording')} ${stamp}.${recordingExtension(blob.type)}`, { type: blob.type })
    files.value.push({ file, kind: 'audio', url: URL.createObjectURL(file) })
  },
  onError: (error) => dictationError(error),
  onSwitch: () => ui.toast(dt('switched'), { kind: 'info' }),
})
watch(dictation.interim, (text) => editor.value?.interim(text))

const kindOf = (file) => (file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'file')
function addFiles(list) {
  for (const file of [...(list || [])]) {
    if (file.size > MAX) {
      ui.warn(t('notes.tooBig', { name: file.name }))
      continue
    }
    const kind = kindOf(file)
    files.value.push({ file, kind, url: kind === 'file' ? null : URL.createObjectURL(file) })
  }
}
function add(event) {
  addFiles(event.target.files)
  event.target.value = ''
}

// A photo or any file dragged onto the note goes with it, as one picked (D-243); text dragged
// within the editor stays the editor's. A picture pasted (a screenshot) goes with it too.
const dragging = ref(false)
const carriesFiles = (e) => [...(e.dataTransfer?.types || [])].includes('Files')
function dragOver(e) {
  if (!carriesFiles(e)) return
  e.preventDefault()
  e.dataTransfer.dropEffect = 'copy'
  dragging.value = true
}
function dragLeave(e) {
  if (!e.currentTarget.contains(e.relatedTarget)) dragging.value = false
}
function dropFiles(e) {
  dragging.value = false
  if (!e.dataTransfer?.files?.length) return
  e.preventDefault()
  e.stopPropagation()
  addFiles(e.dataTransfer.files)
}
function pasteFiles(e) {
  const pictures = [...(e.clipboardData?.files || [])].filter((f) => f.type.startsWith('image/'))
  // Text with a picture beside it (copied from a page) is pasted as text.
  if (!pictures.length || e.clipboardData.getData('text/plain')) return
  e.preventDefault()
  e.stopPropagation()
  const stamp = new Date().toLocaleString('sv-SE').slice(0, 16).replace(':', '.')
  addFiles(pictures.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], `${t('notes.pasted')} ${stamp}${pictures.length > 1 ? ` (${i + 1})` : ''}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type }))))
}
function drop(index) {
  const [removed] = files.value.splice(index, 1)
  if (removed?.url) URL.revokeObjectURL(removed.url)
}
onBeforeUnmount(() => files.value.forEach((f) => f.url && URL.revokeObjectURL(f.url)))

const canSave = computed(() => !!body.value || files.value.length > 0)

// "This was meant to be…" (D-145): a misheard fragment becomes a term, and dictation learns it.
const selected = ref('')
const teaching = ref(null) // {heard, query}
const candidates = computed(() => {
  if (!teaching.value) return []
  const q = teaching.value.query.trim().toLowerCase()
  return (meta.data?.speech_terms || [])
    .filter((t) => !q || t.term.toLowerCase().includes(q))
    .map((t) => ({ term: t.term, d: soundDistance(t.term, teaching.value.heard, t.english) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, 8)
})
function openTeach() {
  teaching.value = { heard: selected.value, query: '' }
}
async function teach(term) {
  const heard = teaching.value.heard
  teaching.value = null
  editor.value?.replaceSelection(term)
  try {
    await learnTerm(term, heard)
    ui.success(t('notes.dictation.learned', { term }))
  } catch (e) {
    ui.error(e?.message || t('messages.error', { message: '' }))
  }
}

/** A note of files only still says what it holds. */
function bodyWithFiles() {
  if (body.value) return body.value
  return { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: `${t('notes.attached')}: ${files.value.map((f) => f.file.name).join(', ')}` }] }] }
}

async function save() {
  if (!canSave.value) return
  dictation.stop()
  saving.value = true
  // The last dictated phrases may still be with the accurate model: they belong to this note.
  await dictation.settled()
  editor.value?.settle()
  try {
    const noteBody = bodyWithFiles()
    const result = await saveFilling('note', null, { noteable: { entity: props.entity, uuid: props.uuid }, body: noteBody, is_private: isPrivate.value }, { label: t('entities.note.one') })
    report(result, { success: files.value.length ? t('notes.savedWithFiles', { n: files.value.length }) : t('notes.saved') })
    if (result.status === 'saved' || result.status === 'queued') {
      const attached = files.value.map((f) => f.file)
      if (attached.length) queueNoteFiles(result.uuid, attached).catch((e) => ui.error(`${t('messages.uploadFailed')} ${e?.message || ''}`))
      emit('saved', result.record || { uuid: result.uuid, entity: 'note', body: noteBody, _pending: 1, created_at: new Date().toISOString() })
      body.value = null
      editor.value?.clear()
      files.value.forEach((f) => f.url && URL.revokeObjectURL(f.url))
      files.value = []
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="relative space-y-2" data-testid="note-composer" @dragenter="dragOver" @dragover="dragOver" @dragleave="dragLeave" @drop.capture="dropFiles" @paste.capture="pasteFiles">
    <div v-if="dragging" class="pointer-events-none absolute -inset-1 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-brand-500 bg-brand-50/90 text-sm font-medium text-brand-700 dark:bg-brand-900/70 dark:text-brand-100" data-testid="note-drop">
      <i class="fa-solid fa-cloud-arrow-up mr-2" aria-hidden="true"></i>{{ $t('notes.dropHere') }}
    </div>
    <RichTextEditor ref="editor" v-model="body" mentions compact palette tables dictation :placeholder="$t('timeline.composerPlaceholder')" min-height="3.5rem" @submit="save" @selection="(text) => (selected = text)" />
    <DictationStatus :dictation="dictation" />

    <!-- what goes with the note -->
    <ul v-if="files.length" class="flex flex-wrap gap-2" data-testid="note-files">
      <li v-for="(f, i) in files" :key="i" :class="['relative flex flex-col overflow-hidden rounded-lg border border-default text-xs', f.kind === 'audio' ? 'w-64' : 'w-28']" :data-file="f.file.name">
        <img v-if="f.kind === 'image'" :src="f.url" :alt="f.file.name" class="h-20 w-full object-cover" />
        <video v-else-if="f.kind === 'video'" :src="f.url" class="h-20 w-full bg-black object-cover" muted playsinline preload="metadata"></video>
        <span v-else-if="f.kind === 'audio'" class="flex h-20 items-center bg-[var(--surface-3)] px-2"><audio :src="f.url" controls preload="metadata" class="w-full" :aria-label="f.file.name" data-testid="note-recording"></audio></span>
        <span v-else class="flex h-20 items-center justify-center bg-[var(--surface-3)] text-2xl text-faint"><i class="fa-solid fa-file" aria-hidden="true"></i></span>
        <span class="truncate px-1.5 pt-1">{{ f.file.name }}</span>
        <span class="px-1.5 pb-1 text-faint">{{ formatBytes(f.file.size) }}</span>
        <button type="button" class="absolute right-1 top-1 rounded-full bg-black/60 px-1.5 text-white" :aria-label="$t('common.remove')" @click="drop(i)"><i class="fa-solid fa-xmark text-3xs" aria-hidden="true"></i></button>
      </li>
    </ul>

    <input ref="photoInput" type="file" accept="image/*" capture="environment" class="hidden" data-testid="note-photo-input" @change="add" />
    <input ref="videoInput" type="file" accept="video/*" capture="environment" class="hidden" data-testid="note-video-input" @change="add" />
    <input ref="fileInput" type="file" multiple class="hidden" data-testid="note-file-input" @change="add" />

    <div class="flex flex-wrap items-center gap-1">
      <!-- Red only once the sound reaches the recognition; before that it is starting (what is said then is lost). -->
      <UiButton v-if="dictation.supported" size="sm" variant="ghost" :icon="!dictation.listening.value ? 'fa-microphone' : dictation.live.value ? 'fa-stop' : 'fa-circle-notch fa-spin'" :class="dictation.live.value ? 'text-red-600' : ''"
                :title="dt(dictation.listening.value ? 'stop' : 'start')" :aria-pressed="dictation.listening.value" data-testid="note-dictate" @click="dictation.toggle()">
        <span class="hidden sm:inline">{{ dt(!dictation.listening.value ? 'start' : dictation.live.value ? 'stop' : 'startingShort') }}</span>
      </UiButton>
      <UiButton size="sm" variant="ghost" icon="fa-camera" :title="$t('notes.photo')" data-testid="note-photo" @click="photoInput.click()"><span class="hidden sm:inline">{{ $t('notes.photo') }}</span></UiButton>
      <UiButton size="sm" variant="ghost" icon="fa-video" :title="$t('notes.video')" data-testid="note-video" @click="videoInput.click()"><span class="hidden sm:inline">{{ $t('notes.video') }}</span></UiButton>
      <UiButton size="sm" variant="ghost" icon="fa-paperclip" :title="$t('notes.files')" data-testid="note-file" @click="fileInput.click()"><span class="hidden sm:inline">{{ $t('notes.files') }}</span></UiButton>
      <UiButton v-if="selected && selected.length <= 120" size="sm" variant="ghost" icon="fa-graduation-cap" class="text-brand-700 dark:text-brand-300" :title="$t('notes.dictation.teachHint')" data-testid="note-teach" @mousedown.prevent @click="openTeach">{{ $t('notes.dictation.teach') }}</UiButton>
    </div>
    <UiModal :open="!!teaching" :title="$t('notes.dictation.teachTitle', { heard: teaching?.heard || '' })" size="sm" @close="teaching = null">
      <div v-if="teaching" class="space-y-2">
        <input v-model="teaching.query" class="input" :placeholder="$t('notes.dictation.teachPlaceholder')" data-testid="teach-query" @keydown.enter.prevent="teaching.query.trim() && teach(candidates[0]?.term.toLowerCase() === teaching.query.trim().toLowerCase() ? candidates[0].term : teaching.query.trim())" />
        <ul class="divide-y divide-[var(--border)] rounded-lg border border-default">
          <li v-for="c in candidates" :key="c.term"><button type="button" class="w-full px-3 py-1.5 text-left text-sm hover:bg-[var(--surface-3)]" :data-term="c.term" @click="teach(c.term)">{{ c.term }}</button></li>
          <li v-if="teaching.query.trim() && !candidates.some((c) => c.term.toLowerCase() === teaching.query.trim().toLowerCase())">
            <button type="button" class="w-full px-3 py-1.5 text-left text-sm text-brand-700 hover:bg-[var(--surface-3)] dark:text-brand-300" data-testid="teach-new" @click="teach(teaching.query.trim())"><i class="fa-solid fa-plus mr-1" aria-hidden="true"></i>{{ $t('notes.dictation.teachNew', { term: teaching.query.trim() }) }}</button>
          </li>
        </ul>
        <p class="text-xs text-muted">{{ $t('notes.dictation.teachHelp') }}</p>
      </div>
    </UiModal>
    <div class="flex items-center gap-3">
      <label class="flex items-center gap-1.5 text-xs text-muted"><input v-model="isPrivate" type="checkbox" class="accent-brand-600" /> <i class="fa-solid fa-lock text-3xs" aria-hidden="true"></i> {{ $t('record.private') }}</label>
      <span class="flex-1"></span>
      <UiButton size="sm" variant="primary" icon="fa-paper-plane" :loading="saving" :disabled="!canSave" data-testid="note-save" @click="save">{{ $t('actions.addNote') }}</UiButton>
    </div>
  </div>
</template>
